import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  getDocs,
  getCountFromServer,
  updateDoc,
  deleteDoc,
  doc,
  writeBatch,
  Timestamp,
  type QueryDocumentSnapshot,
  type DocumentData,
} from 'firebase/firestore';

import { db } from '@/lib/firebase';
import type { Notification, NotificationType } from '@/types';
import { buildNotificationLink } from './notificationLink';
import { NOTIFICATION_PAGE_SIZE, UNREAD_COUNT_QUERY_LIMIT } from './notificationBadge';

const KNOWN_TYPES: NotificationType[] = [
  'new_application',
  'screening_completed',
  'interview_scheduled',
  'interview_cancelled',
  'interview_report_ready',
  'candidate_hired',
  'candidate_rejected',
  'candidate_withdrawn',
  'offer_declined',
  'pipeline_error',
  'member_invited',
  'trial_reminder',
  'subscription_activated',
  'subscription_upgraded',
  'subscription_downgraded',
  'subscription_cancellation_scheduled',
  'subscription_canceled_effective',
  'subscription_uncanceled',
  'payment_failed_retry',
  'payment_failed_final',
  'card_expiring_soon',
  'account_suspended_for_payment',
  'cap_warning',
  'cap_reached',
];

function coerceType(raw: string): NotificationType {
  return (KNOWN_TYPES.includes(raw as NotificationType) ? raw : 'new_application') as NotificationType;
}

function mapUiType(type: NotificationType): Notification['uiType'] {
  switch (type) {
    case 'new_application':
    case 'interview_scheduled':
    case 'interview_report_ready':
      return 'info';
    case 'screening_completed':
    case 'candidate_hired':
      return 'success';
    case 'candidate_rejected':
    case 'candidate_withdrawn':
    case 'offer_declined':
      return 'warning';
    case 'pipeline_error':
      return 'error';
    case 'member_invited':
    case 'interview_cancelled':
      return 'warning';
    case 'trial_reminder':
      return 'warning';
    case 'subscription_activated':
    case 'subscription_upgraded':
    case 'subscription_uncanceled':
      return 'success';
    case 'subscription_downgraded':
    case 'subscription_cancellation_scheduled':
    case 'subscription_canceled_effective':
      return 'warning';
    case 'payment_failed_retry':
    case 'card_expiring_soon':
      return 'warning';
    case 'payment_failed_final':
    case 'account_suspended_for_payment':
      return 'error';
    case 'cap_warning':
      return 'warning';
    case 'cap_reached':
      return 'error';
    default:
      return 'info';
  }
}

function mapDoc(docId: string, data: Record<string, unknown>): Notification {
  const type = coerceType(String(data['type'] ?? 'new_application'));
  const payload = (data['payload'] as Notification['payload']) ?? {};
  const createdRaw = data['createdAt'];
  let createdAt: string;
  if (createdRaw instanceof Timestamp) {
    createdAt = createdRaw.toDate().toISOString();
  } else if (typeof createdRaw === 'string') {
    createdAt = createdRaw;
  } else {
    createdAt = new Date().toISOString();
  }

  return {
    id: docId,
    companyId: String(data['companyId'] ?? ''),
    type,
    uiType: mapUiType(type),
    read: Boolean(data['read']),
    createdAt,
    title: String(data['title'] ?? ''),
    message: String(data['message'] ?? ''),
    payload,
    link: buildNotificationLink(type, payload),
  };
}

function sortNotifications(list: Notification[]): Notification[] {
  return [...list].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

function mergeNotifications(...groups: Notification[][]): Notification[] {
  const byId = new Map<string, Notification>();
  for (const group of groups) {
    for (const item of group) {
      byId.set(item.id, item);
    }
  }
  return sortNotifications(Array.from(byId.values()));
}

/** Incoming team invites must stay visible even before the user joins that company. */
export function isIncomingTeamInvitation(notification: Notification): boolean {
  return Boolean(
    notification.type === 'member_invited'
    && notification.payload.invitationId
    && notification.payload.invitationToken
  );
}

export function isNotificationVisible(
  notification: Notification,
  selectedCompanyId: string | undefined
): boolean {
  if (isIncomingTeamInvitation(notification)) return true;
  if (!selectedCompanyId) return false;
  return notification.companyId === selectedCompanyId;
}

export interface NotificationFeedUpdate {
  notifications: Notification[];
  hasMore: boolean;
}

function companyNotificationsQuery(
  uid: string,
  companyId: string,
  pageLimit: number,
  beforeCreatedAt?: string,
) {
  const notificationsRef = collection(db, 'users', uid, 'notifications');
  if (beforeCreatedAt) {
    return query(
      notificationsRef,
      where('companyId', '==', companyId),
      orderBy('createdAt', 'desc'),
      startAfter(Timestamp.fromDate(new Date(beforeCreatedAt))),
      limit(pageLimit),
    );
  }
  return query(
    notificationsRef,
    where('companyId', '==', companyId),
    orderBy('createdAt', 'desc'),
    limit(pageLimit),
  );
}

function invitationNotificationsQuery(uid: string) {
  // No orderBy — avoids composite index `type + createdAt` while it builds;
  // results are sorted client-side in sortNotifications().
  return query(
    collection(db, 'users', uid, 'notifications'),
    where('type', '==', 'member_invited'),
    limit(UNREAD_COUNT_QUERY_LIMIT),
  );
}

/**
 * Live feed scoped to the selected company (+ pending team invitations).
 * Pagination `hasMore` reflects the company query, not the global collection.
 */
export function subscribeToNotifications(
  uid: string,
  companyId: string | undefined,
  onUpdate: (update: NotificationFeedUpdate) => void,
  onError?: (error: Error) => void
): () => void {
  if (!companyId) {
    onUpdate({ notifications: [], hasMore: false });
    return () => undefined;
  }

  let companyNotifications: Notification[] = [];
  let companyHasMore = false;
  let invitationNotifications: Notification[] = [];

  const emit = () => {
    onUpdate({
      notifications: mergeNotifications(companyNotifications, invitationNotifications),
      hasMore: companyHasMore,
    });
  };

  const companyUnsub = onSnapshot(
    companyNotificationsQuery(uid, companyId, NOTIFICATION_PAGE_SIZE),
    (snap) => {
      companyNotifications = sortNotifications(
        snap.docs.map((d) => mapDoc(d.id, d.data() as Record<string, unknown>))
      );
      companyHasMore = snap.docs.length >= NOTIFICATION_PAGE_SIZE;
      emit();
    },
    (err) => {
      onError?.(err instanceof Error ? err : new Error(String(err)));
    }
  );

  const invitationUnsub = onSnapshot(
    invitationNotificationsQuery(uid),
    (snap) => {
      invitationNotifications = sortNotifications(
        snap.docs
          .map((d) => mapDoc(d.id, d.data() as Record<string, unknown>))
          .filter(isIncomingTeamInvitation)
      );
      emit();
    },
    (err) => {
      console.warn('[notifications:invitations]', err.message);
    }
  );

  return () => {
    companyUnsub();
    invitationUnsub();
  };
}

export async function fetchOlderNotifications(
  uid: string,
  companyId: string | undefined,
  beforeCreatedAt: string
): Promise<NotificationFeedUpdate> {
  if (!companyId) {
    return { notifications: [], hasMore: false };
  }

  const snap = await getDocs(
    companyNotificationsQuery(uid, companyId, NOTIFICATION_PAGE_SIZE, beforeCreatedAt)
  );
  const notifications = snap.docs.map((d) => mapDoc(d.id, d.data() as Record<string, unknown>));
  return {
    notifications,
    hasMore: snap.docs.length >= NOTIFICATION_PAGE_SIZE,
  };
}

/** Live unread count for the bell badge (company-scoped documents). */
export function subscribeToCompanyUnreadCount(
  uid: string,
  companyId: string,
  onUpdate: (count: number, hasMore: boolean) => void,
  onError?: (error: Error) => void,
): () => void {
  const q = query(
    collection(db, 'users', uid, 'notifications'),
    where('companyId', '==', companyId),
    where('read', '==', false),
    limit(UNREAD_COUNT_QUERY_LIMIT),
  );

  return onSnapshot(
    q,
    (snap) => {
      onUpdate(snap.size, snap.size >= UNREAD_COUNT_QUERY_LIMIT);
    },
    (err) => {
      onError?.(err instanceof Error ? err : new Error(String(err)));
    },
  );
}

/** Exact unread count for the bell badge (company-scoped + pending invitations). */
export async function fetchVisibleUnreadCount(
  uid: string,
  companyId: string | undefined,
): Promise<{ count: number; hasMore: boolean }> {
  let count = 0;
  let hasMore = false;

  if (companyId) {
    const companyUnread = query(
      collection(db, 'users', uid, 'notifications'),
      where('companyId', '==', companyId),
      where('read', '==', false),
    );
    const snap = await getCountFromServer(companyUnread);
    count += snap.data().count;
  }

  const invSnap = await getDocs(
    query(
      collection(db, 'users', uid, 'notifications'),
      where('type', '==', 'member_invited'),
      where('read', '==', false),
      limit(UNREAD_COUNT_QUERY_LIMIT),
    )
  );
  const invitationUnread = invSnap.docs
    .map((d) => mapDoc(d.id, d.data() as Record<string, unknown>))
    .filter(isIncomingTeamInvitation);
  count += invitationUnread.length;
  if (invSnap.docs.length >= UNREAD_COUNT_QUERY_LIMIT) {
    hasMore = true;
  }

  return { count, hasMore };
}

export function subscribeToUnreadNotifications(
  uid: string,
  onUpdate: (notifications: Notification[]) => void,
  onError?: (error: Error) => void
): () => void {
  const q = query(
    collection(db, 'users', uid, 'notifications'),
    where('read', '==', false),
    orderBy('createdAt', 'desc'),
    limit(UNREAD_COUNT_QUERY_LIMIT)
  );

  return onSnapshot(
    q,
    (snap) => {
      onUpdate(snap.docs.map((d) => mapDoc(d.id, d.data() as Record<string, unknown>)));
    },
    (err) => {
      onError?.(err instanceof Error ? err : new Error(String(err)));
    }
  );
}

export async function markNotificationRead(uid: string, notificationId: string): Promise<void> {
  await updateDoc(doc(db, 'users', uid, 'notifications', notificationId), { read: true });
}

const MARK_ALL_READ_BATCH_SIZE = 500;

export async function markAllNotificationsRead(
  uid: string,
  notificationIds: string[]
): Promise<void> {
  if (notificationIds.length === 0) return;

  for (let i = 0; i < notificationIds.length; i += MARK_ALL_READ_BATCH_SIZE) {
    const batch = writeBatch(db);
    const chunk = notificationIds.slice(i, i + MARK_ALL_READ_BATCH_SIZE);
    for (const notificationId of chunk) {
      batch.update(doc(db, 'users', uid, 'notifications', notificationId), { read: true });
    }
    await batch.commit();
  }
}

export async function markAllVisibleNotificationsRead(
  uid: string,
  selectedCompanyId: string | undefined,
  extraUnreadIds: string[] = []
): Promise<void> {
  const notificationsRef = collection(db, 'users', uid, 'notifications');
  const idsToMark = new Set<string>(extraUnreadIds);
  let cursor: QueryDocumentSnapshot<DocumentData> | undefined;

  while (true) {
    const q = cursor
      ? query(
          notificationsRef,
          where('read', '==', false),
          orderBy('createdAt', 'desc'),
          startAfter(cursor),
          limit(MARK_ALL_READ_BATCH_SIZE)
        )
      : query(
          notificationsRef,
          where('read', '==', false),
          orderBy('createdAt', 'desc'),
          limit(MARK_ALL_READ_BATCH_SIZE)
        );

    const snap = await getDocs(q);
    if (snap.empty) break;

    for (const notificationDoc of snap.docs) {
      const notification = mapDoc(
        notificationDoc.id,
        notificationDoc.data() as Record<string, unknown>
      );
      if (isNotificationVisible(notification, selectedCompanyId)) {
        idsToMark.add(notificationDoc.id);
      }
    }

    if (snap.docs.length < MARK_ALL_READ_BATCH_SIZE) break;
    cursor = snap.docs[snap.docs.length - 1];
  }

  await markAllNotificationsRead(uid, Array.from(idsToMark));
}

export async function deleteNotification(uid: string, notificationId: string): Promise<void> {
  await deleteDoc(doc(db, 'users', uid, 'notifications', notificationId));
}

export { mergeNotifications, sortNotifications };
